import { differenceInCalendarDays } from "date-fns";
import {
  DayPicker,
  labelNext,
  labelPrevious,
  useDayPicker,
  type DayPickerProps,
} from "react-day-picker";
import { cn } from "@/utils/tailwind";
import { buttonVariants } from "../helpers/button";
import { ChevronLeftIcon, ChevronRightIcon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Dispatch,
  HTMLAttributes,
  PropsWithChildren,
  SetStateAction,
  TableHTMLAttributes,
  useState,
} from "react";

type NavView = "days" | "years";
type SetNavView = Dispatch<SetStateAction<NavView>>;
type DisplayYears = { from: number; to: number };

interface CommonProps {
  className?: string;
  displayYears: DisplayYears;
  startMonth?: Date;
  endMonth?: Date;
  setNavView: SetNavView;
  navView: NavView;
}

export type CalendarProps = DayPickerProps & {
  yearRange?: number;
  showYearSwitcher?: boolean;
};

export function Calendar({
  className,
  showOutsideDays = true,
  showYearSwitcher = true,
  yearRange = 12,
  numberOfMonths,
  startMonth,
  endMonth,
  onNextClick,
  onPrevClick,
  ...props
}: CalendarProps) {
  const [navView, setNavView] = useState<NavView>("days");
  const [displayYears, setDisplayYears] = useState(() => {
    const y = new Date().getFullYear();
    return {
      from: y - Math.floor(yearRange / 2 - 1),
      to: y + Math.ceil(yearRange / 2),
    };
  });

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      style={{
        width: 248.8 * (navView === "years" ? 1 : numberOfMonths ?? 1) + "px",
      }}
      classNames={{
        months: "relative flex",
        month_caption: "relative mx-10 flex h-7 items-center justify-center",
        weekdays: "flex flex-row",
        weekday: "w-8 text-sm font-normal text-muted-foreground",
        month: "w-full",
        caption: "relative flex items-center justify-center pt-1",
        caption_label: "truncate text-sm font-medium",
        button_next: cn(
          buttonVariants({
            variant: "secondary",
            className:
              "absolute h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          }),
          "right-0",
        ),
        button_previous: cn(
          buttonVariants({
            variant: "secondary",
            className:
              "absolute h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          }),
          "left-0",
        ),
        nav: "flex items-start",
        month_grid: "mx-auto mt-4",
        week: "mt-2 flex w-max items-start",
        day: "flex size-8 flex-1 items-center justify-center p-0 text-sm",
        day_button: cn(
          buttonVariants({ variant: "tertiary", hue: "grey" }),
          "size-8 rounded-md p-0 font-normal transition-none aria-selected:opacity-100",
        ),
        range_start:
          "bg-accent [&>button]:bg-brand-500 [&>button]:text-accent [&>button]:hover:bg-brand-500 [&>button]:hover:text-accent rounded-s-md",
        range_middle:
          "bg-accent !text-foreground [&>button]:bg-transparent [&>button]:!text-foreground [&>button]:hover:bg-transparent [&>button]:hover:text-accent",
        range_end:
          "bg-accent [&>button]:bg-brand-500 [&>button]:text-accent [&>button]:hover:bg-brand-500 [&>button]:hover:text-accent rounded-e-md",
        selected:
          "[&>button]:text-accent [&>button]:bg-brand-500 [&>button]:hover:bg-brand-500 [&>button]:hover:text-accent",
        today: "[&>button]:ring-1 [&>button]:ring-brand-200",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50",
        hidden: "invisible flex-1",
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />,
        Nav: ({ className }) => (
          <Nav
            className={className}
            navView={navView}
            startMonth={startMonth}
            endMonth={endMonth}
            displayYears={displayYears}
            setDisplayYears={setDisplayYears}
            onPrevClick={onPrevClick}
            onNextClick={onNextClick}
          />
        ),
        CaptionLabel: (p) => (
          <CaptionLabel
            showYearSwitcher={showYearSwitcher}
            navView={navView}
            setNavView={setNavView}
            displayYears={displayYears}
            {...p}
          />
        ),
        MonthGrid: ({ className, children, ...props }) => (
          <MonthGrid
            children={children}
            className={className}
            displayYears={displayYears}
            startMonth={startMonth}
            endMonth={endMonth}
            navView={navView}
            setNavView={setNavView}
            {...props}
          />
        ),
      }}
      numberOfMonths={navView === "years" ? 1 : numberOfMonths}
      {...props}
    />
  );
}

interface NavProps extends Omit<CommonProps, "setNavView"> {
  setDisplayYears: Dispatch<SetStateAction<DisplayYears>>;
  onPrevClick?: (date: Date) => void;
  onNextClick?: (date: Date) => void;
}

function Nav({
  className,
  navView,
  startMonth,
  endMonth,
  displayYears,
  setDisplayYears,
  onPrevClick,
  onNextClick,
}: NavProps) {
  const intl = useIntl();
  const { nextMonth, previousMonth, goToMonth } = useDayPicker();
  const isPreviousDisabled =
    navView === "years"
      ? (startMonth &&
          differenceInCalendarDays(
            new Date(displayYears.from - 1, 0, 1),
            startMonth,
          ) < 0) ||
        (endMonth &&
          differenceInCalendarDays(
            new Date(displayYears.from - 1, 0, 1),
            endMonth,
          ) > 0)
      : !previousMonth;
  const isNextDisabled =
    navView === "years"
      ? (startMonth &&
          differenceInCalendarDays(
            new Date(displayYears.to + 1, 0, 1),
            startMonth,
          ) < 0) ||
        (endMonth &&
          differenceInCalendarDays(
            new Date(displayYears.to + 1, 0, 1),
            endMonth,
          ) > 0)
      : !nextMonth;

  const handlePreviousClick = () => {
    if (navView === "years") {
      setDisplayYears((p) => ({
        from: p.from - (p.to - p.from + 1),
        to: p.to - (p.to - p.from + 1),
      }));
      onPrevClick?.(
        new Date(
          displayYears.from - (displayYears.to - displayYears.from),
          0,
          1,
        ),
      );
    } else if (previousMonth) {
      goToMonth(previousMonth);
      onPrevClick?.(previousMonth);
    }
  };

  const handleNextClick = () => {
    if (navView === "years") {
      setDisplayYears((p) => ({
        from: p.from + (p.to - p.from + 1),
        to: p.to + (p.to - p.from + 1),
      }));
      onNextClick?.(
        new Date(
          displayYears.from + (displayYears.to - displayYears.from),
          0,
          1,
        ),
      );
    } else if (nextMonth) {
      goToMonth(nextMonth);
      onNextClick?.(nextMonth);
    }
  };

  return (
    <nav
      className={cn("flex items-center", className)}
      aria-label={intl.formatMessage({ defaultMessage: "Calendar navigation" })}
    >
      <Button
        variant="secondary"
        className="absolute left-0 h-7 w-7 bg-transparent p-0 opacity-80 hover:opacity-100"
        type="button"
        tabIndex={isPreviousDisabled ? undefined : -1}
        disabled={isPreviousDisabled}
        aria-label={
          navView === "years"
            ? intl.formatMessage(
                {
                  defaultMessage: "Go to the previous {to} - {from} years",
                },
                { to: displayYears.to, from: displayYears.from + 1 },
              )
            : labelPrevious(previousMonth)
        }
        onClick={handlePreviousClick}
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="secondary"
        className="absolute right-0 h-7 w-7 bg-transparent p-0 opacity-80 hover:opacity-100"
        type="button"
        tabIndex={isNextDisabled ? undefined : -1}
        disabled={isNextDisabled}
        aria-label={
          navView === "years"
            ? intl.formatMessage(
                {
                  defaultMessage: "Go to the next {to} - {from} years",
                },
                { to: displayYears.to, from: displayYears.from + 1 },
              )
            : labelNext(nextMonth)
        }
        onClick={handleNextClick}
      >
        <ChevronRightIcon className="h-4 w-4" />
      </Button>
    </nav>
  );
}

interface CaptionLabelProps
  extends Pick<CommonProps, "setNavView" | "displayYears" | "navView">,
    HTMLAttributes<HTMLSpanElement> {
  showYearSwitcher?: boolean;
}

function CaptionLabel({
  children,
  showYearSwitcher,
  navView,
  setNavView,
  displayYears,
  ...props
}: CaptionLabelProps) {
  if (!showYearSwitcher)
    return (
      <span aria-live="polite" {...props}>
        {children}
      </span>
    );
  return (
    <Button
      aria-live="polite"
      className="h-7 w-full truncate text-sm font-medium"
      variant="tertiary"
      size="sm"
      onClick={() => setNavView((p) => (p === "days" ? "years" : "days"))}
    >
      {navView === "days" ? (
        children
      ) : (
        <FormattedMessage
          defaultMessage="{from} - {to}"
          values={{ from: displayYears.from, to: displayYears.to }}
        />
      )}
    </Button>
  );
}

interface MonthGridProps
  extends CommonProps,
    PropsWithChildren,
    TableHTMLAttributes<HTMLTableElement> {}

function MonthGrid({
  className,
  children,
  displayYears,
  startMonth,
  endMonth,
  navView,
  setNavView,
  ...props
}: MonthGridProps) {
  if (navView === "years")
    return (
      <YearGrid
        displayYears={displayYears}
        startMonth={startMonth}
        endMonth={endMonth}
        setNavView={setNavView}
        navView={navView}
        className={className}
        {...props}
      />
    );
  return (
    <table className={className} role="grid" {...props}>
      {children}
    </table>
  );
}

interface YearGridProps extends CommonProps, HTMLAttributes<HTMLDivElement> {}

function YearGrid({
  className,
  displayYears,
  startMonth,
  endMonth,
  setNavView,
  navView,
  ...props
}: YearGridProps) {
  const intl = useIntl();
  const { goToMonth, selected } = useDayPicker<DayPickerProps>();
  return (
    <div
      className={cn("grid grid-cols-4 gap-y-2", className)}
      role="grid"
      aria-label={intl.formatMessage({ defaultMessage: "Year selection" })}
      {...props}
    >
      {Array.from(
        { length: displayYears.to - displayYears.from + 1 },
        (_, i) => {
          const y = displayYears.from + i;
          const isBefore =
            startMonth &&
            differenceInCalendarDays(new Date(y, 11, 31), startMonth) < 0;
          const isAfter =
            endMonth &&
            differenceInCalendarDays(new Date(y, 0, 0), endMonth) > 0;
          return (
            <Button
              key={i}
              className={cn(
                "h-7 w-full text-sm font-normal text-foreground",
                y === new Date().getFullYear() &&
                  "bg-accent font-medium text-accent-foreground",
              )}
              variant="tertiary"
              onClick={() => {
                setNavView("days");
                const month =
                  selected instanceof Date
                    ? selected.getMonth()
                    : Array.isArray(selected)
                      ? selected[0]?.getMonth()
                      : selected?.from?.getMonth() || 0;
                goToMonth(new Date(y, month));
              }}
              disabled={navView === "years" ? isBefore || isAfter : undefined}
            >
              {y}
            </Button>
          );
        },
      )}
    </div>
  );
}
